import { useCallback, useMemo } from 'react';
import {
  getAllSegments,
  getClipFPS,
  getClipReadOnlyPlayerConfig,
  getIsDuplicateRecording,
  isNotFullLengthClip,
  isOriginalLayoutAvailable,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import { getEditsForSegment, getScenesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import {
  buildTrimmedClipForCreate,
  TrimmedClipForCreate
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/buildTrimmedClipForCreate';
import { hasCompositeSegmentBetweenSegmentIds } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/createClipValidation';
import { isAudioContent } from '@/stores/core';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { showErrorToast } from '@/libs/toast/toast';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';

const useCreateClip = ({ clip }: { clip: Clip }) => {
  const { createClip } = useTranscriptPage();

  const isCreateClipAllowed = useMemo(() => {
    if (isNotFullLengthClip(clip) && !getIsDuplicateRecording(clip)) {
      return false;
    }
    return true;
  }, [clip]);

  const getDefaultLayout = useCallback(() => {
    if (isAudioContent()) {
      return 'AUDIOGRAM';
    }
    if (isOriginalLayoutAvailable(clip)) {
      return 'DEFAULT';
    }
    return 'GRID';
  }, [clip]);

  const onCreateClip = useCallback(
    (
      startTime: number | null,
      endTime: number | null,
      startSegmentId?: string,
      endSegmentId?: string,
      isDuplicate?: boolean,
      title?: string
    ) => {
      const isClipDuplicationEnabled = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Clip_Duplication];
      const isSplitsEnabled = isSplitsInTimelineEnabledPure({
        clip: clip,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const hasMultipleScenes = isSplitsEnabled || (clip.asset_metadata.scenes || []).length > 1;
      if (isSplitsEnabled && startSegmentId && endSegmentId) {
        const allSegments = getAllSegments(clip);
        const fps = getClipFPS(clip);

        if (startTime === null || endTime === null) {
          return Promise.reject(new Error('Start time or end time is null'));
        }

        const hasComposite = hasCompositeSegmentBetweenSegmentIds(allSegments, startSegmentId, endSegmentId);

        if (!hasComposite) {
          showErrorToast('At least one recording segment is required between the selected start and end time.');
          return Promise.reject(new Error('At least one composite segment is required'));
        }

        // Splits already supports a trimmed player_config today; this path is not gated.
        let trimmed: TrimmedClipForCreate | null;
        try {
          trimmed = buildTrimmedClipForCreate({
            clip,
            startTime,
            endTime,
            startSegmentId,
            endSegmentId,
            fps
          });
        } catch (err) {
          return Promise.reject(err);
        }
        if (!trimmed) {
          return Promise.reject(new Error('Unable to build a trimmed clip for the selected range'));
        }

        const additionalParams: Record<string, any> = {
          ...(trimmed.playerConfig ? { player_config: trimmed.playerConfig } : {}),
          layout: hasMultipleScenes ? clip.asset_metadata.layout : getDefaultLayout(),
          duration: trimmed.duration,
          start: trimmed.start,
          end: trimmed.end,
          captions: trimmed.captions
        };

        return createClip(trimmed.minStartTime, trimmed.maxEndTime, {
          edits: trimmed.edits,
          deletes: trimmed.deletes,
          scenes: undefined,
          fps,
          additionalParams,
          isDuplicate,
          title
        });
      }

      if (startTime === null || endTime === null) {
        return Promise.reject(new Error('Start time or end time is null'));
      }

      let clipDataWithUpdatedLayout: Clip;
      try {
        clipDataWithUpdatedLayout = hasMultipleScenes
          ? clip
          : (updateClipData(
              {
                clipId: clip.id,
                actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
                layout: getDefaultLayout()
              },
              {
                skipStoreAndBackendUpdate: true
              }
            ) as Clip);
      } catch (err) {
        return Promise.reject(err);
      }

      const playerConfig = getClipReadOnlyPlayerConfig(clipDataWithUpdatedLayout);

      return createClip(startTime, endTime, {
        edits: getEditsForSegment(clipDataWithUpdatedLayout),
        deletes: getDeletesForSegmentPure({
          clip: clipDataWithUpdatedLayout,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }),
        scenes: getScenesForSegment(clipDataWithUpdatedLayout),
        fps: getClipFPS(clip),
        additionalParams: {
          layout: hasMultipleScenes ? clipDataWithUpdatedLayout.asset_metadata.layout : getDefaultLayout(),
          ...(isClipDuplicationEnabled && playerConfig ? { player_config: playerConfig } : {})
        },
        isDuplicate,
        title
      });
    },
    [clip, createClip, getDefaultLayout]
  );

  const onCreateDuplicate = useCallback(
    (name?: string) => {
      // A duplicate spans the whole clip: from the first segment's start to the last segment's end.
      // Passing the first/last segment ids drives onCreateClip's splits path, which replays the full
      // timeline (both trims become no-ops) and preserves every segment along with its metadata.
      // The duplicated_from_* tag is threaded through so the created clip is marked as a duplicate,
      // and the user-supplied name becomes the new recording's title.

      const allSegments = getAllSegments(clip);
      const isSplitsEnabled = isSplitsInTimelineEnabledPure({
        clip,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });

      if (!isSplitsEnabled || allSegments.length === 0) {
        return onCreateClip(clip.asset_metadata.start, clip.asset_metadata.end, undefined, undefined, true, name);
      }

      const firstSegment = allSegments[0];
      const lastSegment = allSegments[allSegments.length - 1];
      const fps = getClipFPS(clip);
      const firstSegmentStartTime = firstSegment.fromFrame / fps;
      const lastSegmentEndTime = lastSegment.toFrame / fps;

      return onCreateClip(firstSegmentStartTime, lastSegmentEndTime, firstSegment.id, lastSegment.id, true, name);
    },
    [clip, onCreateClip]
  );

  return { isCreateClipAllowed, onCreateClip, onCreateDuplicate };
};

export default useCreateClip;
